// What JobQ says is happening at a station, shaped for the cameras pages.
// Sources (both jobq-api, both existing):
//   GET /stations/activity                     — live, recomputed from tasks
//   GET /stations/{station}/timeline/{date}    — the day's state blocks
// "Right now" always comes from /stations/activity: station_state_log can keep
// an interval open for days when no task event fires, so the timeline is only
// used for what already happened.

import { fmtDuration } from './cameraRelay';

/** "Zachary Schwalm" → "Zachary S.", "Brady" → "Brady". */
export function shortName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

const tidyDesc = (d) => String(d || '').replace(/\s+-\s+/g, ' · ').trim();

/**
 * One station's live state → {kind, text, meta, job, user}.
 * kind: working | paused | maintenance | waiting | idle | unknown.
 */
export function describeStation(row) {
  if (!row) return { kind: 'unknown', text: '', meta: '' };
  const tasks = Array.isArray(row.tasks) ? row.tasks : [];
  const since = fmtDuration(row.elapsedSecondsInState);
  const pick = (status) => tasks.find((t) => t.status === status);

  if (row.status === 'Maintenance') {
    const t = pick('Maintenance') || tasks[0];
    return {
      kind: 'maintenance',
      text: `Maintenance${t?.maintenanceReason ? ` · ${t.maintenanceReason}` : t?.jobDescription ? ` · ${tidyDesc(t.jobDescription)}` : ''}`,
      meta: since,
      job: t?.jobNumber ?? null,
      jobId: t?.jobId ?? null,
      user: shortName(t?.userName),
    };
  }
  if (row.status === 'In Progress') {
    const working = tasks.filter((t) => t.status === 'In Progress');
    const t = working[0] || tasks[0];
    const more = working.length > 1 ? ` (+${working.length - 1} more)` : '';
    const user = shortName(t?.userName);
    return {
      kind: 'working',
      text: t ? `Job ${t.jobNumber} · ${tidyDesc(t.jobDescription)}${more}` : 'Working',
      meta: [user, since].filter(Boolean).join(' · '),
      job: t?.jobNumber ?? null,
      jobId: t?.jobId ?? null,
      user,
    };
  }
  if (row.status === 'Paused') {
    const t = pick('Paused') || tasks[0];
    return {
      kind: 'paused',
      text: t ? `Paused · Job ${t.jobNumber} · ${tidyDesc(t.jobDescription)}` : 'Paused',
      meta: since,
      job: t?.jobNumber ?? null,
      jobId: t?.jobId ?? null,
      user: shortName(t?.userName),
    };
  }
  if (row.status === 'Waiting') {
    return { kind: 'waiting', text: 'No one on it · work scheduled today', meta: since, job: null, user: '' };
  }
  return { kind: 'idle', text: 'Idle', meta: '', job: null, user: '' };
}

/** /stations/activity payload → { [stationName]: describeStation(row) }. */
export function activityByStation(payload) {
  const out = {};
  if (!Array.isArray(payload)) return out;
  for (const row of payload) {
    if (row && row.stationName) out[row.stationName] = describeStation(row);
  }
  return out;
}

// ── Day timeline ──

const CENTRAL = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

/** Minute of the Central day `ymd` that an ISO instant falls on (clamped 0–1440). */
export function minuteOfCentralDay(iso, ymd) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = {};
  for (const { type, value } of CENTRAL.formatToParts(d)) p[type] = value;
  const day = `${p.year}-${p.month}-${p.day}`;
  if (day < ymd) return 0;
  if (day > ymd) return 1440;
  return Number(p.hour) * 60 + Number(p.minute) + Number(p.second) / 60;
}

/**
 * /stations/{station}/timeline/{date} → Working and Maintenance blocks,
 * [{start, end, state, job, jobId, desc, user, product}] in Central minutes.
 * Waiting / Idle / At Risk blocks are dropped: on a camera timeline only
 * "someone was doing something" matters.
 */
export function timelineBlocks(timeline, ymd) {
  if (!Array.isArray(timeline)) return [];
  const out = [];
  for (const b of timeline) {
    if (b.state !== 'Working' && b.state !== 'Maintenance') continue;
    const start = minuteOfCentralDay(b.start_time, ymd);
    const end = minuteOfCentralDay(b.end_time, ymd);
    if (start === null || end === null || end <= start) continue;
    const t = (b.tasks || []).find((x) => x && x.master_job_num != null) || null;
    out.push({
      start,
      end,
      state: b.state,
      job: t ? t.master_job_num : null,
      desc: t ? tidyDesc(t.job_description) : '',
      user: t ? shortName(t.user_name) : '',
      product: t ? (t.product_id || '') : '',
    });
  }
  return out.sort((a, b) => a.start - b.start);
}

/**
 * Join back-to-back blocks of the same job (a pause, a second operator punch,
 * a state flip) into one run, so "Job 9890" reads as one 1 h 37 min stretch
 * instead of three. Working blocks with no job (state flips) are dropped.
 */
export function mergeJobRuns(blocks, gapMinutes = 3) {
  const runs = [];
  for (const b of blocks) {
    if (b.state === 'Working' && b.job == null) continue;
    const last = runs[runs.length - 1];
    if (last && last.state === b.state && last.job === b.job && b.start - last.end <= gapMinutes) {
      last.end = Math.max(last.end, b.end);
      if (!last.user && b.user) last.user = b.user;
      continue;
    }
    runs.push({ ...b });
  }
  return runs;
}

/** The run covering minute `t`, or null. */
export function runAt(runs, t) {
  return runs.find((r) => r.start <= t && t < r.end) || null;
}

/**
 * The day's jobs for a jump list: one row per stretch of one job. Maintenance
 * stops (nozzle changes, laser checks) between two blocks of the same job are
 * folded into it and counted; a maintenance stretch between different jobs
 * gets its own row only when it lasts `minMaintMinutes` or more. Without this
 * a laser check reads as eight one-minute "Job / Maintenance" rows.
 */
export function jobListRows(blocks, { gapMinutes = 15, minMaintMinutes = 2 } = {}) {
  const rows = [];
  let row = null;
  let pending = []; // maintenance blocks since the last job block

  const flushPending = () => {
    if (!pending.length) return;
    const start = pending[0].start;
    const end = pending[pending.length - 1].end;
    if (end - start >= minMaintMinutes) {
      rows.push({ state: 'Maintenance', job: null, desc: '', user: '', product: '', start, end, stops: 0 });
    }
    pending = [];
  };

  for (const b of blocks || []) {
    if (b.state !== 'Working') {
      pending.push(b);
      continue;
    }
    if (b.job == null) continue;
    if (row && row.job === b.job && b.start - row.end <= gapMinutes) {
      row.stops += pending.filter((m) => m.end - m.start >= 1).length;
      pending = [];
      row.end = Math.max(row.end, b.end);
      if (!row.user && b.user) row.user = b.user;
      continue;
    }
    flushPending();
    row = { state: 'Working', job: b.job, desc: b.desc, user: b.user, product: b.product, start: b.start, end: b.end, stops: 0 };
    rows.push(row);
  }
  flushPending();
  return rows.sort((a, b) => a.start - b.start);
}
